import { useState, type ReactElement, type ReactNode } from 'react';
import { Popover, Portal, useSlotRecipe, type Popover as ChakraPopover } from '@chakra-ui/react';
import { popoverSlotRecipe } from '../../theme/recipes/popover';

export type RiftboundPopoverPlacement = NonNullable<
  NonNullable<ChakraPopover.RootProps['positioning']>['placement']
>;

export type RiftboundPopoverVariant = 'menu' | 'panel' | 'elevated';

export interface RiftboundPopoverProps {
  trigger: ReactElement;
  children: ReactNode;
  placement?: RiftboundPopoverPlacement;
  width?: string | number;
  minW?: string | number;
  maxW?: string | number;
  /** Popover.Body padding — `0` for menu lists, `2` for filter panels. */
  bodyPadding?: number | string;
  /**
   * - `menu`: action menus (deck export, kebab)
   * - `panel`: toolbar / filter dropdowns
   * - `elevated`: account menu and other prominent overlays
   */
  variant?: RiftboundPopoverVariant;
  /** Stacking above modals (e.g. card sheet collection prompt). */
  positionerZIndex?: number;
  /**
   * Set to `false` when rendering inside a Dialog — portalling the positioner
   * moves it outside the dialog's DOM subtree and breaks the focus trap.
   */
  portal?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Standard Chakra popover shell used across toolbars and action menus.
 * Pair with {@link FilterMenuButton} when you need the filter pill trigger.
 */
export function RiftboundPopover({
  trigger,
  children,
  placement = 'bottom-end',
  width = '260px',
  minW,
  maxW,
  bodyPadding = 0,
  variant = 'menu',
  positionerZIndex,
  portal = true,
  open: controlledOpen,
  onOpenChange,
}: RiftboundPopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const recipe = useSlotRecipe({ recipe: popoverSlotRecipe });
  const chrome = recipe({ variant }).content;

  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const positioner = (
    <Popover.Positioner zIndex={positionerZIndex}>
      <Popover.Content
        w={width}
        minW={minW}
        maxW={maxW}
        p={0}
        css={chrome}
        // Chakra caps height with --available-height; scroll instead of clipping.
        overflowX="hidden"
        overflowY="auto"
      >
        <Popover.Body p={bodyPadding}>{children}</Popover.Body>
      </Popover.Content>
    </Popover.Positioner>
  );

  return (
    <Popover.Root
      open={open}
      onOpenChange={(d) => setOpen(d.open)}
      positioning={{ placement }}
    >
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      {portal ? <Portal>{positioner}</Portal> : positioner}
    </Popover.Root>
  );
}
